import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { RESULT_INDEX_LABEL, resultHeadline, weakestTrackLine } from "../copy";

interface ResultProps {
  /** The AI-first index, 0 to 100. */
  index: number;
  /** The level label for that index. */
  level: string;
  /**
   * The lowest-scoring track's name as it reads mid-sentence, already cased by
   * the content ("AI-first operating model"), so nothing lowercases it here.
   */
  weakestTrackInSentence: string;
  /**
   * True once the user has answered at least once in this session: the headline
   * takes focus so a screen reader lands on the index instead of nothing. False
   * on a restored session, where stealing focus would be unexpected.
   */
  takeFocus: boolean;
  reducedMotion: boolean;
  /**
   * Embedded from 900px the result shares a 680px column with the portrait,
   * so the index is capped to keep the whole block inside it.
   */
  embedded?: boolean;
}

const COUNT_UP_MS = 1100;

/** The closing screen: the index, counting up, the headline and the weakest track. */
export function Result({
  index,
  level,
  weakestTrackInSentence,
  takeFocus,
  reducedMotion,
  embedded = false,
}: ResultProps) {
  const [shown, setShown] = useState(reducedMotion ? index : 0);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (takeFocus) {
      // The scrolling is the page's to do: a focus that scrolls would move the
      // page around a frame too.
      headingRef.current?.focus({ preventScroll: true });
    }
  }, [takeFocus]);

  useEffect(() => {
    if (reducedMotion) {
      setShown(index);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / COUNT_UP_MS);
      // Ease out cubic.
      setShown(Math.round(index * (1 - Math.pow(1 - progress, 3))));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [index, reducedMotion]);

  return (
    <div className="border-hw-line mt-8 border-t pt-8">
      <p className="hw-mono">{RESULT_INDEX_LABEL}</p>

      {/* The one light weight on the page, after the landing page's own
          display headline: 300, tight tracking, tight leading. */}
      <p
        className={cn(
          "mt-2 text-[clamp(64px,8vw,110px)] leading-[0.9] font-light tracking-[-0.065em]",
          embedded && "min-[900px]:text-[72px]",
        )}
      >
        {/* The number changes sixty times a second while it counts up, so it is
            hidden and pinned to `off`: the final value is announced once, by
            the sr-only span beside it and again by the headline below. */}
        <span aria-hidden="true" aria-live="off">
          {shown}
        </span>
        <span className="sr-only">{index}</span>
        <span className="text-hw-mute ml-2 text-[16px] font-normal tracking-normal">/ 100</span>
      </p>

      <h2 ref={headingRef} tabIndex={-1} className="hw-h3 mt-4 max-w-[26ch] outline-none">
        {resultHeadline(level)}
      </h2>

      <p className="hw-body text-hw-soft mt-4">{weakestTrackLine(weakestTrackInSentence)}</p>
    </div>
  );
}
